import { Spacing } from '@persona-id/design-tokens';
import { PlusIcon, XIcon } from '@persona-id/design-tokens/icons';
import { Selection } from '@react-types/shared';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import Divider from 'dashboard/components/Divider';
import ReactSelect from 'dashboard/components/ReactSelect';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { CaseVariableDefinition } from 'dashboard/models/case-variable-definition';
import { ModuleDataTable } from 'dashboard/modules/DataTable';
import { DataTableColumn, DataTableColumnFormat } from 'dashboard/modules/DataTable/model';
import { IconButton } from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import ListBox from 'design-system/components/ListBox';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label } from 'design-system/components/Text';

const FORMAT_OPTIONS = [{ label: 'String', value: 'string' }];

const MAXIMIZABLE_PANEL_STYLES = css`
  width: 500px;
  min-height: initial;
  padding: ${Spacing[1]};
`;

const EditColumnInput = styled(Input)`
  min-width: 250px;
`;

const EditColumnSelect = styled(Select)`
  min-width: 250px;
  max-width: fit-content;
`;

const ColumnLabel = styled(Label)`
  overflow-x: hidden;
  text-overflow: ellipsis;
`;

const StyledDivider = styled(Divider)`
  margin-top: 0;
  margin-bottom: ${Spacing[1]};
`;

const StyledHStack = styled(HStack)`
  width: 100%;
`;

interface DataTablePanelProps {
  module: ModuleDataTable;
  updateModule: (module: ModuleDataTable) => void;
  variableDefinitions: CaseVariableDefinition[];
}

const DataTablePanel = ({ module, updateModule, variableDefinitions }: DataTablePanelProps) => {
  // TODO (Amy): delete if block after fixing data table module data in staging + prod
  if (module.columns == null) {
    module.columns = [];
  }

  const [selectedColumnToken, setSelectedColumnToken] = useState<string | null>();
  const selectedColumn = module.columns.find((column) => column.token === selectedColumnToken);

  // TODO(Ernest): refactor this to be generated through case data manager schema select
  const variableOptions = variableDefinitions.map((variableDefinition) => ({
    label: variableDefinition.attributes.key,
    value: variableDefinition.id,
  }));

  const updateVariableToken = (variableToken: string) => {
    updateModule({
      ...module,
      'variable-token': variableToken,
    });
  };

  const handleAdd = () => {
    const token = `dtcol_${uuid()}`;
    updateModule({
      ...module,
      columns: [
        ...module.columns,
        {
          key: '',
          format: 'string',
          label: `Column ${module.columns.length + 1}`,
          token: `dtcol_${uuid()}`,
        },
      ],
    });
    setSelectedColumnToken(token);
  };

  const handleDelete = (token: string) => {
    updateModule({
      ...module,
      columns: module.columns.filter((column) => column.token !== token),
    });
    setSelectedColumnToken(null);
  };

  const handleUpdate = (partialColumn: Partial<DataTableColumn>) => {
    const nextColumns = module.columns.reduce((acc, column) => {
      if (column.token === selectedColumnToken) {
        acc.push({ ...column, ...partialColumn });
      } else {
        acc.push(column);
      }
      return acc;
    }, [] as DataTableColumn[]);

    updateModule({
      ...module,
      columns: nextColumns,
    });
  };

  const handleClose = () => {
    setSelectedColumnToken(null);
  };

  return (
    <>
      <ControlPanelContainer>
        <Heading size='sm'>Content</Heading>
        <Spacer height={2} />
        <HStack align='center' equal>
          <Label size='md' weight='bold' id='data_table_panel__variable-definition-selector--label'>
            Query
          </Label>
          <ReactSelect
            id='data_table_panel__variable-definition-selector'
            aria-labelledby='data_table_panel__variable-definition-selector--label'
            value={module['variable-token']}
            options={variableOptions}
            onChange={updateVariableToken}
            portal={true}
          />
        </HStack>
        <HStack align='center' equal>
          <Label size='md' weight='bold'>
            Columns
          </Label>
          <Spacer width={6} />
          <IconButton id='extraction-panel-list-box__add-column-button' onClick={handleAdd}>
            <PlusIcon />
          </IconButton>
        </HStack>
        <ListBox
          selectionMode='single'
          selectionBehavior='toggle'
          items={module.columns}
          onSelectionChange={(selection: Selection) => {
            if (selection !== 'all') {
              setSelectedColumnToken(selection.values().next().value?.toString());
            }
          }}
          emptyText='None configured'
          fullWidth
        >
          {module.columns.map((column) => (
            <ListBox.Item key={column.token} id={column.token} aria-label={column.label}>
              <StyledHStack align='center' equal>
                <ColumnLabel size='md'>{column.label}</ColumnLabel>
                <IconButton
                  id='extraction-panel-list-box__delete-column-button'
                  onClick={() => handleDelete(column.token)}
                >
                  <XIcon />
                </IconButton>
              </StyledHStack>
            </ListBox.Item>
          ))}
        </ListBox>
      </ControlPanelContainer>
      {selectedColumn != null && (
        <MaximizablePanel
          title='Edit column'
          offsetElementId='control-panel'
          outsideExceptionalIdPrefixes={[`data-table-panel__column-${selectedColumnToken}`]}
          onOutsideClickAndEscPress={handleClose}
          rightButton={
            <HStack align='center' equal>
              <IconButton id='extraction-panel-list-box__close-button' onClick={handleClose}>
                <XIcon />
              </IconButton>
            </HStack>
          }
          containerStyleOverride={MAXIMIZABLE_PANEL_STYLES}
        >
          <StyledDivider full />
          <HStack align='center' equal>
            <Label id='data_table_panel__edit-column-configuration--label' size='md'>
              Column label
            </Label>
            <EditColumnInput
              role='input'
              value={selectedColumn.label}
              onChange={(e) => {
                handleUpdate({ label: e.target.value });
              }}
              aria-labelledby='data_table_panel__edit-column-configuration--label'
            />
          </HStack>
          <Spacer height={1} />
          <HStack align='center' equal>
            <Label id='data_table_panel__edit-column-configuration--key' size='md'>
              Key
            </Label>
            <EditColumnInput
              role='input'
              value={selectedColumn.key}
              onChange={(e) => {
                handleUpdate({ key: e.target.value });
              }}
              aria-labelledby='data_table_panel__edit-column-configuration--key'
            />
          </HStack>
          <Spacer height={1} />
          <HStack align='center' equal>
            <Label id='data_table_panel__edit-column-configuration--format' size='md'>
              Format
            </Label>
            <EditColumnSelect
              portal={true}
              value={selectedColumn.format}
              onValueChange={(value: DataTableColumnFormat) => handleUpdate({ format: value })}
              aria-labelledby='data_table_panel__edit-column-configuration--format'
            >
              {FORMAT_OPTIONS.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </EditColumnSelect>
          </HStack>
        </MaximizablePanel>
      )}
    </>
  );
};

export default DataTablePanel;
